/**
 * Sync-time cloud categorization upgrade.
 *
 * When the user creates an expense offline, the category comes from
 * the free tiers only (keyword detector or on-device LLM — cloud is
 * skipped during typing because it requires connectivity and costs
 * money per call). At sync time we're back online, the
 * `predict-category` endpoint's DynamoDB cache will absorb most
 * repeats for free, and it's the natural moment to upgrade the
 * categorization before the expense lands in the database.
 *
 * Rules — only call the cloud iff ALL of:
 *  - the user did not pick the category manually
 *    (tracked via `predictedCategorySource`; absent ⇒ manual)
 *  - the existing source isn't already 'cloud'
 *  - the live scheduler would not have trusted the free-tier answer
 *    (`isTrustedLivePrediction`: keyword ≥ 0.8 or native ≥ 0.85, never
 *    `'other'`) — the same gate applied at entry time, so nothing the
 *    app already accepted for free is re-paid here
 *  - the title is long enough to bother classifying
 *
 * The function never throws and never blocks sync — any failure path
 * returns the original expense unchanged, so a flaky predict endpoint
 * doesn't stop expenses from syncing.
 */
import type { PendingExpense } from '@splitfiesta/shared';

import {
  MIN_PREDICTION_TITLE_CHARS,
  isTrustedLivePrediction,
} from '../../hooks/useCategoryPredictionScheduler';
import { CLOUD_MIN_CONFIDENCE } from '../categoryPrediction/cloudConfidence';
import {
  EXPENSE_TAXONOMY_VERSION,
  isValidExpensePrediction,
} from '../categoryPrediction/taxonomy';

/**
 * Shape returned by `ApiClientService.expenses.predictCategory` once
 * unwrapped from the `SuccessResponseDto`. Callers should adapt their
 * client wrapper to this shape (return `null` on 502/503/auth so the
 * upgrade gracefully skips).
 */
export interface CloudCategoryPrediction {
  category: string;
  subcategory: string;
  confidence: number;
  taxonomyVersion: number;
}

export interface MaybeUpgradeCategoryArgs {
  expense: PendingExpense;
  locale: string;
  /**
   * Wrapper around `ApiClientService.expenses.predictCategory`.
   * Should resolve to `null` on any non-success outcome (network
   * error, 502 invalid prediction, 503 not configured / degraded,
   * 401 auth) — never throw, so the sync loop stays simple.
   */
  predictCategory: (input: {
    title: string;
    locale: string;
  }) => Promise<CloudCategoryPrediction | null>;
}

export async function maybeUpgradeCategoryWithCloud(
  args: MaybeUpgradeCategoryArgs
): Promise<PendingExpense> {
  const { expense } = args;

  // Manual pick — never overwrite.
  if (expense.predictedCategorySource === undefined) return expense;
  // Already cloud — no benefit; calling again would re-spend without
  // changing the answer.
  if (expense.predictedCategorySource === 'cloud') return expense;

  // Same gate the live scheduler applied at entry time: an answer the
  // app trusted for free then is not re-paid now.
  const trustedAtEntry =
    !!expense.category &&
    isTrustedLivePrediction({
      category: expense.category,
      confidence: expense.predictedCategoryConfidence ?? 0,
      source: expense.predictedCategorySource,
    });
  if (trustedAtEntry) return expense;

  const trimmedTitle = expense.title?.trim() ?? '';
  if (trimmedTitle.length < MIN_PREDICTION_TITLE_CHARS) return expense;

  try {
    const cloud = await args.predictCategory({
      title: trimmedTitle,
      locale: args.locale,
    });
    if (!cloud) return expense;
    if (cloud.confidence < CLOUD_MIN_CONFIDENCE) return expense;
    // An API on another taxonomy version (mid-rollout) can answer with keys
    // this client no longer has, or has under a different category.
    if (cloud.taxonomyVersion !== EXPENSE_TAXONOMY_VERSION) return expense;
    const sub = cloud.subcategory?.trim() ? cloud.subcategory : null;
    if (!isValidExpensePrediction(cloud.category, sub)) return expense;

    // The old subcategory belongs to the old category, so it only survives
    // when the cloud confirms that category.
    const subcategory =
      cloud.category === expense.category ? (sub ?? expense.subcategory) : sub;

    return {
      ...expense,
      category: cloud.category,
      subcategory,
      predictedCategorySource: 'cloud',
      predictedCategoryConfidence: cloud.confidence,
    };
  } catch {
    // Cloud unreachable / 5xx / circuit breaker / unexpected throw —
    // keep the free-tier value. The upgrade is best-effort; sync
    // must never block on it.
    return expense;
  }
}
